import { useState, useEffect, FormEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Monitor,
  Smartphone,
  LogOut,
  Eye,
  EyeOff,
  Copy,
  Check,
  KeyRound,
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from "../../components/Card/Card";
import { Tabs } from "../../components/Tabs/Tabs";
import { Input } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Modal } from "../../components/Modal/Modal";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/Toast";
import {
  PasswordStrengthMeter,
  scorePassword,
} from "../auth/PasswordStrengthMeter";
import { useAuth } from "../auth/useAuth";
import { apiClient } from "../../lib/apiClient";
import { ApiError, describeError } from "../../lib/errors";
import {
  updateName,
  changePassword,
  listSessions,
  deleteSession,
  AdminSession,
} from "./api";
import { YourProjectsCard } from "./YourProjectsCard";
import { humanizeRelativeDate } from "../projects/ProjectsListPage";
import styles from "./AccountPage.module.css";

// Name policy mirrors the mockup helper string (account.html L418):
// "Letters, digits, spaces and dashes · 2–60 chars."
const NAME_PATTERN = /^[A-Za-z0-9 -]+$/;
const NAME_MIN = 2;
const NAME_MAX = 60;

export function AccountPage() {
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Account" }]}
        title="Your account"
      />
      <div className={styles.page}>
        <Tabs
          tabs={[
            { id: "profile", label: "Profile", content: <ProfileTab /> },
            {
              id: "security",
              label: "Password & sessions",
              content: <SecurityTab />,
            },
          ]}
          defaultId="profile"
        />
      </div>
    </>
  );
}

/* ───────────────────────────── Profile tab ────────────────────────────── */

function ProfileTab() {
  const qc = useQueryClient();
  const { user, projects } = useAuth();
  const { push } = useToast();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch sessions purely to source a "Last sign-in" hint (latest session row
  // for the caller). The backend `me` payload does NOT carry last_sign_in_at;
  // until it does, we surface the most-recent session's last_seen_at + meta.
  const { data: sessions = [] } = useQuery({
    queryKey: ["sessions"],
    queryFn: listSessions,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  if (!user) return null;

  const trimmed = name.trim();
  const dirty = trimmed !== user.name;
  const validLen = trimmed.length >= NAME_MIN && trimmed.length <= NAME_MAX;
  const validPattern = NAME_PATTERN.test(trimmed);
  const valid = validLen && validPattern;
  const nameError = !dirty
    ? null
    : !validLen
      ? `Use ${NAME_MIN}–${NAME_MAX} characters.`
      : !validPattern
        ? "Only letters, digits, spaces and dashes are allowed."
        : null;

  const latest = pickLatestSession(sessions);
  const lastSignInRel = latest
    ? humanizeRelativeDate(latest.last_seen_at)
    : "—";
  const lastSignInDetails = latest
    ? [
        latest.ip || null,
        prettyUserAgent(latest.ua),
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  const isSuper = user.role === "super_admin";
  const roleLabel = isSuper ? "Super admin" : "Member";
  const roleDescriptor = isSuper
    ? "All projects, all settings"
    : "Project member access";

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!dirty || !valid) return;
    setError(null);
    setSaving(true);
    try {
      await updateName(trimmed);
      await qc.invalidateQueries({ queryKey: ["me"] });
      push({ variant: "success", message: "Profile updated." });
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        push({
          variant: "warn",
          message:
            "Your profile was updated elsewhere. Reload to see the latest.",
        });
        await qc.invalidateQueries({ queryKey: ["me"] });
      } else {
        setError(
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setName(user?.name ?? "");
    setError(null);
  }

  const accountId = user.id;
  async function copyAccountId() {
    try {
      await navigator.clipboard.writeText(accountId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — clipboard may be unavailable in some browsers
    }
  }

  return (
    <div className={styles.stack}>
      <form onSubmit={save} noValidate>
        <Card>
          <CardHeader>
            <div>
              <div className={styles.cardTitle}>Profile</div>
              <div className={styles.cardSub}>
                Used in invites, team list, activity log.
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {error && (
              <div className={styles.alertWrap}>
                <Alert variant="danger">{error}</Alert>
              </div>
            )}

            <FieldRow
              name="Display name"
              hint="Shown next to your activity. Anyone who can see you (teammates, platform admins) sees this."
              labelFor="account-display-name"
            >
              <Input
                id="account-display-name"
                aria-label="Display name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={nameError ?? undefined}
                hint={
                  nameError
                    ? undefined
                    : "Letters, digits, spaces and dashes · 2–60 chars."
                }
              />
            </FieldRow>

            <FieldRow
              name="Email address"
              hint="Sign-in identifier. Cannot be changed — contact a super admin if needed."
              labelFor="account-email"
            >
              <Input
                id="account-email"
                aria-label="Email address"
                value={user.email}
                readOnly
              />
            </FieldRow>

            <FieldRow
              name="Role"
              hint="Platform-level role."
            >
              <div className={styles.staticValue}>
                <Badge variant={isSuper ? "admin" : "user"}>{roleLabel}</Badge>
                <span className={styles.staticValueDescriptor}>
                  {roleDescriptor}
                </span>
              </div>
            </FieldRow>

            <FieldRow
              name="Account ID"
              hint="For SDK debugging and support tickets."
            >
              <div className={styles.staticValue}>
                <code className={styles.accountId} data-testid="account-id">
                  {user.id}
                </code>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={copyAccountId}
                  aria-label="Copy account ID"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
            </FieldRow>

            <FieldRow name="Last sign-in">
              <div className={styles.staticValue}>
                <span className={styles.staticValueMain}>{lastSignInRel}</span>
                {lastSignInDetails && (
                  <span className={styles.staticValueDescriptor}>
                    · {lastSignInDetails}
                  </span>
                )}
              </div>
            </FieldRow>
          </CardBody>
          <CardFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={discard}
              disabled={!dirty || saving}
            >
              Discard
            </Button>
            <Button type="submit" disabled={!dirty || !valid || saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </CardFooter>
        </Card>
      </form>

      <YourProjectsCard projects={projects} isSuper={isSuper} />
    </div>
  );
}

/* ──────────────────────── Password & sessions tab ─────────────────────── */

function SecurityTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push } = useToast();

  // change password
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaving, setPwSaving] = useState(false);

  // Strength gating — mirror the Reset page submit policy (length ≥ 12,
  // letter+digit) so weak passwords can't even attempt the round-trip.
  const score = scorePassword(next);
  const hasLetter = /[a-zA-Z]/.test(next);
  const hasDigit = /[0-9]/.test(next);
  const strongEnough =
    next.length >= 12 && hasLetter && hasDigit && score.level !== "weak";
  const matches = confirm.length > 0 && confirm === next;
  const mismatch = confirm.length > 0 && confirm !== next;
  const formValid =
    current.length > 0 && strongEnough && matches && !pwSaving;

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setPwError(null);
    if (!formValid) {
      if (mismatch) setPwError("New passwords don't match.");
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(current, next);
      push({
        variant: "success",
        message: "Password changed. Other sessions were signed out.",
      });
      setCurrent("");
      setNext("");
      setConfirm("");
      await qc.invalidateQueries({ queryKey: ["sessions"] });
    } catch (err) {
      setPwError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    } finally {
      setPwSaving(false);
    }
  }

  function discardPassword() {
    setCurrent("");
    setNext("");
    setConfirm("");
    setPwError(null);
  }

  // sessions
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: listSessions,
  });

  const deleteSessionMut = useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      push({ variant: "success", message: "Session signed out." });
    },
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  async function signOutAll() {
    setConfirmAllOpen(false);
    try {
      await apiClient.post("/api/admin/auth/sign-out-all");
    } catch {
      // ignore — we redirect either way
    }
    navigate("/login", { replace: true });
  }

  // "Current session" detection — the backend marks the session backing this
  // device's cookie with `is_current`.

  return (
    <div className={styles.stack}>
      <form onSubmit={onChangePassword} noValidate>
        <Card>
          <CardHeader>
            <div>
              <div className={styles.cardTitle}>Change password</div>
              <div className={styles.cardSub}>
                All other sessions will be signed out when you save.
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {pwError && (
              <div className={styles.alertWrap}>
                <Alert variant="danger">{pwError}</Alert>
              </div>
            )}

            <FieldRow
              name="Current password"
              hint="Required to confirm it's you."
              labelFor="account-current-pw"
            >
              <Input
                id="account-current-pw"
                aria-label="Current password"
                type={showCurrent ? "text" : "password"}
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                trailingIcon={
                  <button
                    type="button"
                    className={styles.toggleVis}
                    aria-label={
                      showCurrent ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowCurrent((v) => !v)}
                    tabIndex={-1}
                  >
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              <div className={styles.fieldHelperRow}>
                <span />
                <span>
                  Forgot it?{" "}
                  <Link to="/forgot">
                    Reset via email (you'll be signed out everywhere).
                  </Link>
                </span>
              </div>
            </FieldRow>

            <FieldRow
              name="New password"
              hint="Min 12 characters · at least one letter and one digit."
              labelFor="account-new-pw"
            >
              <Input
                id="account-new-pw"
                aria-label="New password"
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                trailingIcon={
                  <button
                    type="button"
                    className={styles.toggleVis}
                    aria-label={
                      showNew ? "Hide new password" : "Show new password"
                    }
                    onClick={() => setShowNew((v) => !v)}
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              <div className={styles.meterWrap}>
                <PasswordStrengthMeter password={next} />
              </div>
            </FieldRow>

            <FieldRow
              name="Confirm new password"
              labelFor="account-confirm-pw"
            >
              <Input
                id="account-confirm-pw"
                aria-label="Confirm new password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                trailingIcon={
                  <button
                    type="button"
                    className={styles.toggleVis}
                    aria-label={
                      showConfirm
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                    onClick={() => setShowConfirm((v) => !v)}
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              {mismatch && (
                <div
                  className={`${styles.matchHelper} ${styles.err}`}
                  role="status"
                >
                  Passwords don't match
                </div>
              )}
              {matches && (
                <div
                  className={`${styles.matchHelper} ${styles.ok}`}
                  role="status"
                >
                  <Check size={12} /> Passwords match
                </div>
              )}
            </FieldRow>
          </CardBody>
          <CardFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={discardPassword}
              disabled={
                pwSaving || (!current && !next && !confirm)
              }
            >
              Discard
            </Button>
            <Button type="submit" disabled={!formValid}>
              {pwSaving ? "Updating…" : "Change password"}
            </Button>
          </CardFooter>
        </Card>
      </form>

      <Card>
        <CardHeader>
          <div>
            <div className={styles.cardTitle}>Active sessions</div>
            <div className={styles.cardSub}>
              Where your account is currently signed in. Signing out one
              revokes its cookie.
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className={styles.muted}>Loading…</div>
          ) : sessions.length === 0 ? (
            <div className={styles.muted}>No active sessions.</div>
          ) : (
            <ul className={styles.sessionList} role="list">
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  isCurrent={s.is_current}
                  onRevoke={() => deleteSessionMut.mutate(s.id)}
                  revoking={deleteSessionMut.isPending}
                />
              ))}
            </ul>
          )}
        </CardBody>
        <CardFooter>
          <Button
            variant="danger-outline"
            onClick={() => setConfirmAllOpen(true)}
          >
            <LogOut size={14} aria-hidden="true" />
            <span>Sign out everywhere</span>
          </Button>
        </CardFooter>
      </Card>

      <Alert variant="info" title="Two-factor authentication">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <KeyRound size={14} aria-hidden="true" />
          2FA / TOTP is coming in a future release. We'll prompt you to enrol
          when it's available — for now, use a strong unique password and the
          Sessions list to spot anything unusual.
        </span>
      </Alert>

      <Modal
        open={confirmAllOpen}
        onClose={() => setConfirmAllOpen(false)}
        title="Sign out of all sessions, including this one?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmAllOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={signOutAll}>
              Sign out everywhere
            </Button>
          </>
        }
      >
        You'll be signed back to the login page. Other devices will also lose
        their session.
      </Modal>
    </div>
  );
}

/* ─────────────────────────── helpers / sub-views ──────────────────────── */

function FieldRow({
  name,
  hint,
  labelFor,
  children,
}: {
  name: string;
  hint?: string;
  labelFor?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.fieldRow}>
      <div className={styles.fieldLabel}>
        {labelFor ? (
          <label className={styles.fieldName} htmlFor={labelFor}>
            {name}
          </label>
        ) : (
          <div className={styles.fieldName}>{name}</div>
        )}
        {hint && <div className={styles.fieldHint}>{hint}</div>}
      </div>
      <div className={styles.fieldInput}>{children}</div>
    </div>
  );
}

function SessionRow({
  session,
  isCurrent,
  onRevoke,
  revoking,
}: {
  session: AdminSession;
  isCurrent: boolean;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isMobile = looksLikeMobile(session.ua);
  return (
    <li className={styles.sessionRow}>
      <span
        className={`${styles.deviceIcon} ${
          isCurrent ? styles.deviceIconCurrent : ""
        }`}
        aria-hidden="true"
      >
        {isMobile ? <Smartphone size={18} /> : <Monitor size={18} />}
      </span>
      <div className={styles.sessionInfo}>
        <div className={styles.sessionDevice}>
          <span>{prettyUserAgent(session.ua) || "Unknown device"}</span>
          {isCurrent && (
            <span className={styles.thisDevice}>this device</span>
          )}
        </div>
        <div className={styles.sessionMeta}>
          {(session.ip || "—") +
            " · last seen " +
            humanizeRelativeDate(session.last_seen_at)}
        </div>
      </div>
      {!isCurrent && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onRevoke}
          disabled={revoking}
          aria-label={`Sign out ${prettyUserAgent(session.ua) || "session"}`}
        >
          Sign out
        </Button>
      )}
    </li>
  );
}

function pickLatestSession(
  sessions: AdminSession[],
): AdminSession | null {
  if (sessions.length === 0) return null;
  let latest = sessions[0];
  let latestT = new Date(latest.last_seen_at).getTime();
  for (const s of sessions.slice(1)) {
    const t = new Date(s.last_seen_at).getTime();
    if (!Number.isNaN(t) && t > latestT) {
      latest = s;
      latestT = t;
    }
  }
  return latest;
}

/**
 * Cheap, dependency-free UA parser. Surfaces "Chrome on macOS", "Mobile
 * Safari on iOS", "Firefox on Linux" — enough for the sessions list. Returns
 * "" for empty / unrecognized UA strings so the caller can fall back.
 */
export function prettyUserAgent(ua: string | undefined | null): string {
  if (!ua) return "";
  const browser =
    /Edg\//.test(ua)
      ? "Edge"
      : /OPR\/|Opera/.test(ua)
        ? "Opera"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : /Chrome\//.test(ua) && !/Edg\//.test(ua)
            ? "Chrome"
            : /Safari\//.test(ua) && !/Chrome\//.test(ua)
              ? /Mobile/.test(ua)
                ? "Mobile Safari"
                : "Safari"
              : null;
  const os =
    /Windows NT/.test(ua)
      ? "Windows"
      : /Mac OS X|Macintosh/.test(ua)
        ? "macOS"
        : /iPhone|iPad|iOS/.test(ua)
          ? "iOS"
          : /Android/.test(ua)
            ? "Android"
            : /Linux/.test(ua)
              ? "Linux"
              : null;
  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  return ua.slice(0, 40);
}

function looksLikeMobile(ua: string | undefined | null): boolean {
  if (!ua) return false;
  return /Mobile|iPhone|iPad|Android/.test(ua);
}
